import { UserType } from '../types/user';

/**
 * 判断用户是否为超级管理员
 * @param userInfo 用户信息
 * @returns 是否为超级管理员
 */
export function isAdmin(userInfo: UserType | undefined): boolean {
  if (!userInfo) return false;

  return (
    userInfo.id === 1 &&
    userInfo.username === 'admin' &&
    userInfo.expire_date === '9999-99-99'
  );
}

// 权限控制配置
export const PERMISSION_CONFIG = {
  // 允许未授权用户访问的路由
  ALLOWED_ROUTES_WITHOUT_AUTH: [
    '/dashboard',
    '/fbp-duration',
    '/store-authorization',
    '/user/cate',
    '/admin/noon_order/list',
    '/bestSell',
    '/extension',
  ],
  // 授权页面路由
  AUTH_ROUTE: '/store-authorization',
};

/**
 * 检查路由是否需要权限验证
 * @param targetPath 目标路径
 * @returns 是否需要权限验证
 */
export function needsPermissionCheck(targetPath: string): boolean {
  return !PERMISSION_CONFIG.ALLOWED_ROUTES_WITHOUT_AUTH.includes(targetPath);
}

/**
 * 检查用户账号是否已过期
 * @param userInfo 用户信息
 * @returns 是否已过期
 */
export function isAccountExpired(userInfo: UserType | undefined): boolean {
  if (!userInfo) return false;
  return userInfo.status === -1;
}

/**
 * 检查用户是否有权限访问指定路由
 * @param userInfo 用户信息
 * @param targetPath 目标路径
 * @returns 是否有权限访问
 */
export function hasRoutePermission(
  userInfo: UserType | undefined,
  targetPath: string
): boolean {
  if (process.env.NODE_ENV === 'development') {
    console.log('权限检查:', { userInfo, targetPath });
  }

  // 如果用户信息不存在，无权限
  if (!userInfo) {
    if (process.env.NODE_ENV === 'development') {
      console.log('用户信息不存在，无权限');
    }
    return false;
  }

  // 检查账号是否过期（status为-1时账号过期）
  if (isAccountExpired(userInfo)) {
    if (process.env.NODE_ENV === 'development') {
      console.log('账号已过期，拒绝访问');
    }
    return false;
  }

  // 如果是管理员，拥有所有权限
  if (isAdmin(userInfo)) {
    if (process.env.NODE_ENV === 'development') {
      console.log('管理员用户，拥有所有权限');
    }
    return true;
  }

  // 如果用户已授权，拥有所有权限
  if (userInfo.is_auth === true) {
    if (process.env.NODE_ENV === 'development') {
      console.log('用户已授权，拥有所有权限');
    }
    return true;
  }

  // 如果用户未授权，只能访问指定路由
  if (userInfo.is_auth === false) {
    const hasPermission =
      PERMISSION_CONFIG.ALLOWED_ROUTES_WITHOUT_AUTH.includes(targetPath);
    if (process.env.NODE_ENV === 'development') {
      console.log('用户未授权，检查允许的路由:', { targetPath, hasPermission });
    }
    return hasPermission;
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('默认拒绝访问');
  }
  // 默认拒绝访问
  return false;
}

/**
 * 获取权限检查结果
 * @param userInfo 用户信息
 * @param targetPath 目标路径
 * @returns 权限检查结果
 */
export function checkPermission(
  userInfo: UserType | undefined,
  targetPath: string
): {
  hasPermission: boolean;
  redirectTo?: string;
  reason?: string;
} {
  // 检查账号是否过期
  if (userInfo && isAccountExpired(userInfo)) {
    return {
      hasPermission: false,
      reason: 'account_expired',
    };
  }

  const hasPermission = hasRoutePermission(userInfo, targetPath);

  if (!hasPermission && userInfo?.is_auth === false) {
    return {
      hasPermission: false,
      redirectTo: PERMISSION_CONFIG.AUTH_ROUTE,
      reason: 'unauthorized',
    };
  }

  return {
    hasPermission,
  };
}
